import { MonoLabel } from '../primitives';

export interface SeatUsageBarProps {
  /** Currently-occupied seats (active members + pending invitations). */
  used: number;
  /** Cap for this tier. When `>= Number.MAX_SAFE_INTEGER` (matches the
   *  seeded `2_147_483_647` for the t500 "uncapped" tier) the bar is hidden
   *  and the label switches to "N seats used". */
  max: number;
  /** Optional `data-testid` for the wrapper. */
  dataTestId?: string;
}

const UNCAPPED_THRESHOLD = 1_000_000;

/**
 * Seat-usage meter for the `/settings/billing` Current-tier card.
 *
 * Renders one of two shapes:
 * - **Capped tier:** "N of M seats used" label above a thin progress bar.
 *   Fill width is `clamp(0, used/max, 1)` mapped to a percentage. At
 *   `used >= max` the fill saturates to 100% so the bar reads "full" rather
 *   than overflowing.
 * - **Uncapped tier (t500):** "N seats used" label only, no bar.
 *
 * Visual values are tokens — the surface uses `--surface-2` as the track, the
 * fill uses `--accent`, with the at-limit variant tinted by `--st-blocked`.
 * Lives under `components/billing/` per the cluster brief: it only renders on
 * the billing surface, so v1 keeps it domain-scoped. If it gets reused
 * elsewhere it graduates to `components/primitives/`.
 */
export function SeatUsageBar({
  used,
  max,
  dataTestId
}: SeatUsageBarProps) {
  const uncapped = max >= UNCAPPED_THRESHOLD;
  if (uncapped) {
    return (
      <div className="pn-seat-usage" data-testid={dataTestId} data-uncapped="true">
        <MonoLabel size="sm" tracking="wide" tone="muted">
          {used} {used === 1 ? 'seat used' : 'seats used'}
        </MonoLabel>
      </div>
    );
  }
  const ratio = max > 0 ? Math.min(1, Math.max(0, used / max)) : 0;
  const pct = Math.round(ratio * 100);
  const atLimit = max > 0 && used >= max;
  return (
    <div
      className="pn-seat-usage"
      data-testid={dataTestId}
      data-at-limit={atLimit ? 'true' : 'false'}
    >
      <MonoLabel size="sm" tracking="wide" tone="muted">
        {used} of {max} {max === 1 ? 'seat used' : 'seats used'}
      </MonoLabel>
      <div
        className="pn-seat-usage-track"
        role="progressbar"
        aria-valuenow={used}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label="Seat usage"
      >
        {/* width is runtime data (used/max), not a design literal */}
        <div
          className="pn-seat-usage-fill"
          style={{ width: `${pct}%` }}
          aria-hidden
        />
      </div>
    </div>
  );
}
